<template>
	<div class="order-confirm-page">
		<!-- 背景圖片（置中） -->
		<div class="bg-image-wrapper">
			<img
				:src="require('@/assets/order_pending.svg')"
				alt=""
				class="bg-image"
			/>
		</div>
		<ByFooter />
	</div>
</template>

<script>
import store from '@/store'
import {
	cleanupExpiredTransactions,
	clearTransaction,
	isTransactionProcessed,
	markTransactionProcessed,
} from '@/utils/transactionGuard'
import ByFooter from '@/components/Footer.vue'

export default {
	name: 'OrderConfirm',
	components: { ByFooter },
	// 路由守衛：防止用戶通過瀏覽器返回鍵回到此頁面
	beforeRouteEnter(to, from, next) {
		// 先清理過期的交易記錄
		cleanupExpiredTransactions()

		const { code, orderPaymentId } = to.query
		const storeHash = to.params.random

		// 如果缺少必要參數，說明是非正常進入（如：按返回鍵），拒絕進入
		if (!code || !orderPaymentId) {
			console.warn('[OrderConfirm] 缺少必要參數，拒絕進入')
			// 設置待顯示的訊息到 store，然後阻止導航並重定向
			store.commit('setPendingAlert', {
				message: '付款確認參數錯誤',
				type: 'danger',
			})
			next({ path: `/${storeHash}/Store`, replace: true })
			return
		}

		// 檢查交易是否已處理過
		if (isTransactionProcessed(orderPaymentId)) {
			console.warn('[OrderConfirm] 交易已處理，拒絕重複進入')
			// 設置待顯示的訊息到 store，然後阻止導航並重定向
			store.commit('setPendingAlert', {
				message: '此交易已處理，無法重複確認',
				type: 'warning',
			})
			next({ path: `/${storeHash}/Order`, replace: true })
			return
		}

		// 不在路由守衛階段標記交易，改為在 API 成功後才標記
		// 這樣失敗時用戶可以重試
		next()
	},
	data() {
		return {
			apiCalled: false, // 防重複調用標記（組件級）
			redirectPath: '', // 關閉後跳轉路徑
		}
	},
	created() {
		// 1. 防重複調用檢查（組件層級）
		if (this.apiCalled) {
			console.warn('[OrderConfirm] API already called, skip')
			return
		}
		this.apiCalled = true

		// 2. 取得路由參數
		const storeHash = this.$storeManager.qrCodeHash
		const query = this.$route.query
		const orderPaymentId = query.orderPaymentId

		// 3. 立即標記交易為處理中，防止組件重複載入時的競態條件
		markTransactionProcessed(orderPaymentId)

		// 4. 使用全域 Loading 並確認付款交易
		this.$loading.show({
			message: '確認付款中...',
			successText: '付款成功',
			errorText: '付款失敗',
			resultDuration: 2000,
			onClose: () => this.handleRedirect(),
		})

		this.$api.order
			.confirmTransaction(query.code, {
				storeHash,
				...query,
			})
			.then(res => {
				const {
					data: { hashId, paymentStatus, message },
				} = res

				// API 成功，保持交易標記（已在步驟 3 標記）
				if (paymentStatus === 'SUCCEEDED') {
					this.$store.commit('user/SET_CART_ITEM', [])
					this.$store.commit('myOrder/SET_FINISH_ORDER', true)
					// NOTE: 送出成功後，清空取餐方式的選擇和所有相關紀錄（除電話和姓名）
					this.$store.commit('user/RESET_PICKUPMETHOD')
					this.$store.commit('user/CLEAR_PICKUP_SELECTIONS')
					this.$loading.success()
					this.redirectPath = `/${storeHash}/TakeMeal/${hashId}`
				} else if (paymentStatus === 'FAILED') {
					this.$loading.error('付款失敗')
					this.redirectPath = `/${storeHash}/Order`
				} else {
					// 其他狀態，使用後端回傳的訊息
					this.$loading.error(message || '付款發生異常')
					this.redirectPath = `/${storeHash}/Order`
				}
			})
			.catch(err => {
				console.error('[OrderConfirm] 付款確認失敗:', err)
				// API 失敗時清除交易標記，允許用戶重試
				clearTransaction(orderPaymentId)
				// 嘗試取得後端錯誤訊息
				const errorMessage =
					err?.response?.data?.message || err?.message || '付款出現異常!'
				this.$loading.error(errorMessage)
				this.redirectPath = `/${storeHash}/Order`
			})
	},
	methods: {
		handleRedirect() {
			if (this.redirectPath) {
				this.$router.replace(this.redirectPath)
			}
		},
	},
}
</script>

<style scoped>
.order-confirm-page {
	height: calc(100vh - 50px);
	overflow: hidden;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	position: relative;
}

.bg-image-wrapper {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	justify-content: center;
	align-items: center;
	background-color: #ffffff;
}

.bg-image {
	max-width: 80%;
	max-height: 60%;
	object-fit: contain;
}
</style>
